import { useState, useEffect, Suspense, lazy } from "react";
import { motion, AnimatePresence } from "motion/react";
import SEOManager, { seoData } from "./components/SEO/SEOManager";
import AccessibilityProvider, {
  SkipLink,
} from "./components/Accessibility/AccessibilityProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import Chatbot from "./components/Chatbot";

// Type declarations for global extensions
declare global {
  interface Window {
    __REACT_DEVTOOLS_GLOBAL_HOOK__?: {
      supportsFiber: boolean;
      inject: (fn: any) => void;
      onCommitFiberRoot: (renderer: any, root: any, priorityLevel: any) => void;
      onCommitFiberUnmount: (renderer: any, fiber: any) => void;
      isDisabled: boolean;
    };
  }

  interface PerformanceEntry {
    processingStart?: number;
    hadRecentInput?: boolean;
    value?: number;
  }
}

// Lazy load components for better performance
const HomePage = lazy(() => import("./components/HomePage"));
const PortfolioPage = lazy(() => import("./components/PortfolioPage"));
const PricingPage = lazy(() => import("./components/PricingPage"));
const ServicesPage = lazy(() => import("./components/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./components/ServiceDetailPage"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));

type Page =
  | "home"
  | "portfolio"
  | "pricing"
  | "services"
  | "service-detail"
  | "privacy-policy"
  | "terms-conditions"
  | "contact";

// Loading component with accessibility
function LoadingSpinner() {
  return (
    <div
      className="flex items-center justify-center min-h-screen"
      role="status"
      aria-live="polite"
      aria-label="Loading content"
      data-oid="cq-30pn"
    >
      <div className="relative" data-oid="w3r2uiw">
        <div
          className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"
          data-oid="ml_6b5f"
        ></div>
        <span className="sr-only" data-oid=":s8xune">
          Loading...
        </span>
      </div>
    </div>
  );
}

export default function App() {
  const [currentPage, setCurrentPage] = useState<Page>("home");
  const [currentService, setCurrentService] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  // Service name to slug mapping for SEO-friendly URLs
  const serviceToSlug = (serviceName: string): string => {
    // Create a mapping of service names to their URL slugs
    const serviceMap: { [key: string]: string } = {
      'SEO': 'seo',
      'PPC': 'ppc',
      'Pay-Per-Click Advertising': 'pay-per-click-advertising',
      'Social Media Marketing': 'social-media-marketing',
      'Email Marketing': 'email-marketing',
      'Web Design & Development': 'web-design-development',
      'Brand Strategy & Design': 'brand-strategy-design',
      'AI Automation': 'ai-automation'
    };

    return serviceMap[serviceName] || serviceName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Slug to service name mapping
  const slugToService = (slug: string): string => {
    // Create a mapping of slugs to their proper service names
    const slugMap: { [key: string]: string } = {
      'seo': 'SEO',
      'ppc': 'PPC',
      'pay-per-click-advertising': 'Pay-Per-Click Advertising',
      'social-media-marketing': 'Social Media Marketing',
      'email-marketing': 'Email Marketing',
      'web-design-development': 'Web Design & Development',
      'web-design': 'Web Design & Development',
      'brand-strategy-design': 'Brand Strategy & Design',
      'brand-strategy': 'Brand Strategy & Design',
      'ai-automation': 'AI Automation'
    };

    return slugMap[slug] || slug
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get initial page from URL on mount
  useEffect(() => {
    const path = window.location.pathname;

    if (path === '/' || path === '/home') {
      setCurrentPage('home');
    } else if (path === '/portfolio') {
      setCurrentPage('portfolio');
    } else if (path === '/pricing') {
      setCurrentPage('pricing');
    } else if (path === '/services') {
      setCurrentPage('services');
    } else if (path.startsWith('/services/') && path.length > '/services/'.length) {
      // Extract service slug from URL like /services/web-development
      const serviceSlug = path.replace('/services/', '');
      // Convert slug back to service name
      const serviceName = slugToService(serviceSlug);
      setCurrentPage('service-detail');
      setCurrentService(serviceName);
    } else if (path === '/privacy-policy') {
      setCurrentPage('privacy-policy');
    } else if (path === '/terms-conditions') {
      setCurrentPage('terms-conditions');
    } else if (path === '/contact') {
      setCurrentPage('contact');
    }

    // Handle browser back/forward buttons
    const handlePopState = () => {
      const path = window.location.pathname;

      if (path === '/' || path === '/home') {
        setCurrentPage('home');
      } else if (path === '/portfolio') {
        setCurrentPage('portfolio');
      } else if (path === '/pricing') {
        setCurrentPage('pricing');
      } else if (path === '/services') {
        setCurrentPage('services');
      } else if (path.startsWith('/services/') && path.length > '/services/'.length) {
        // Extract service slug from URL like /services/web-development
        const serviceSlug = path.replace('/services/', '');
        // Convert slug back to service name
        const serviceName = slugToService(serviceSlug);
        setCurrentPage('service-detail');
        setCurrentService(serviceName);
      } else if (path === '/privacy-policy') {
        setCurrentPage('privacy-policy');
      } else if (path === '/terms-conditions') {
        setCurrentPage('terms-conditions');
      } else if (path === '/contact') {
        setCurrentPage('contact');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToPage = (page: Page, service?: string) => {
    setIsLoading(true);
    setCurrentPage(page);
    if (service) setCurrentService(service);

    // Update browser URL with SEO-friendly structure
    let url = '/';
    if (page === 'home') {
      url = '/';
    } else if (page === 'service-detail' && service) {
      // Convert service name to SEO-friendly URL slug
      const serviceSlug = serviceToSlug(service);
      url = `/services/${serviceSlug}`;
    } else {
      url = `/${page}`;
    }

    window.history.pushState({ page, service }, '', url);

    // Scroll to top when navigating to a new page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Simulate loading delay for better UX
    setTimeout(() => {
      setIsLoading(false);
    }, 300);

    // Announce page change to screen readers
    const pageNames = {
      home: "Home page",
      services: "Services page",
      portfolio: "Portfolio page",
      pricing: "Pricing page",
      "service-detail": `${service} service page`,
      "privacy-policy": "Privacy Policy page",
      "terms-conditions": "Terms and Conditions page",
      contact: "Contact Us page",
    };

    setTimeout(() => {
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", "polite");
      announcement.className = "sr-only";
      announcement.textContent = `Navigated to ${pageNames[page] || "new page"}`;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    }, 500);
  };

  // Get current SEO data based on page and service
  const getCurrentSEOData = () => {
    if (currentPage === "service-detail" && currentService) {
      const serviceKey = currentService
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-")
        .replace(/--+/g, "-");
      return seoData[serviceKey as keyof typeof seoData] || seoData.services;
    }
    return seoData[currentPage] || seoData.home;
  };

  // Preload critical resources and setup performance monitoring
  useEffect(() => {
    // Initialize React DevTools hook if not present (development only)
    if (
      typeof window !== "undefined" &&
      process.env.NODE_ENV === "development"
    ) {
      if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === "undefined") {
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
          supportsFiber: true,
          inject: () => { },
          onCommitFiberRoot: () => { },
          onCommitFiberUnmount: () => { },
          isDisabled: false,
        };
      }
    }

    // Preload fonts for better performance
    const fontLink = document.createElement("link");
    fontLink.rel = "preload";
    fontLink.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700;800;900&display=swap";
    fontLink.as = "style";
    fontLink.crossOrigin = "anonymous";
    document.head.appendChild(fontLink);

    // Preconnect to external domains for better performance
    const preconnectDomains = [
      "https://fonts.googleapis.com",
      "https://fonts.gstatic.com",
      "https://images.unsplash.com",
    ];

    preconnectDomains.forEach((domain) => {
      if (!document.querySelector(`link[rel="preconnect"][href="${domain}"]`)) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = domain;
        if (domain.includes("gstatic")) {
          link.crossOrigin = "";
        }
        document.head.appendChild(link);
      }
    });

    // Add performance observer for Core Web Vitals monitoring
    if ("PerformanceObserver" in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            // Log performance metrics for debugging (can be sent to analytics in production)
            if (entry.entryType === "largest-contentful-paint") {
              console.log("LCP:", entry.startTime);
            }
            if (entry.entryType === "first-input" && entry.processingStart) {
              const fid = entry.processingStart - entry.startTime;
              console.log("FID:", fid);
            }
            if (
              entry.entryType === "layout-shift" &&
              entry.hadRecentInput !== true &&
              entry.value !== undefined
            ) {
              console.log("CLS:", entry.value);
            }
          });
        });

        // Observe Core Web Vitals
        observer.observe({
          entryTypes: [
            "largest-contentful-paint",
            "first-input",
            "layout-shift",
          ],
        });

        // Cleanup observer on unmount
        return () => {
          observer.disconnect();
        };
      } catch (error) {
        console.warn(
          "Performance Observer not supported or failed to initialize:",
          error,
        );
      }
    }

    // Setup intersection observer for lazy loading (if not supported by browser)
    if (!("IntersectionObserver" in window)) {
      console.warn(
        "IntersectionObserver not supported, lazy loading will be disabled",
      );
    }

    // Check if user prefers reduced motion
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) {
      document.documentElement.classList.add("reduce-motion");
    }

    // Monitor network connection for performance optimization
    if ("connection" in navigator) {
      const connection = (navigator as any).connection;
      if (connection && connection.effectiveType) {
        console.log("Network connection:", connection.effectiveType);
        // Could adjust image quality or loading strategies based on connection
      }
    }

    // Add error boundary for JavaScript errors
    const handleError = (event: ErrorEvent) => {
      // Filter out React DevTools errors in development
      if (
        process.env.NODE_ENV === "development" &&
        event.error &&
        (event.error.message?.includes("React DevTools") ||
          event.error.message?.includes("__REACT_DEVTOOLS_GLOBAL_HOOK__"))
      ) {
        return; // Ignore React DevTools related errors
      }
      console.error("JavaScript error:", event.error);
      // In production, this could be sent to error tracking service
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      // Filter out React DevTools related promise rejections
      if (
        process.env.NODE_ENV === "development" &&
        event.reason?.toString().includes("React DevTools")
      ) {
        return;
      }
      console.error("Unhandled promise rejection:", event.reason);
      // In production, this could be sent to error tracking service
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    // Setup page visibility API for performance optimization
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, could pause animations or reduce activity
        console.log("Page hidden");
      } else {
        // Page is visible, resume normal activity
        console.log("Page visible");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup event listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  const renderPage = () => {
    if (isLoading) {
      return <LoadingSpinner data-oid="gz6hsgo" />;
    }

    switch (currentPage) {
      case "home":
        return <HomePage onNavigate={navigateToPage} data-oid="js131yp" />;
      case "portfolio":
        return <PortfolioPage onNavigate={navigateToPage} data-oid="jwndotr" />;
      case "pricing":
        return <PricingPage onNavigate={navigateToPage} data-oid="i994xl5" />;
      case "services":
        return <ServicesPage onNavigate={navigateToPage} data-oid=".go1yxn" />;
      case "service-detail":
        return (
          <ServiceDetailPage
            service={currentService}
            onNavigate={navigateToPage}
            data-oid="-6vievt"
          />
        );

      case "privacy-policy":
        return <PrivacyPage onNavigate={navigateToPage} data-oid="8_t9wv." />;
      case "terms-conditions":
        return <TermsPage onNavigate={navigateToPage} />;
      case "contact":
        return <ContactPage onNavigate={navigateToPage} data-oid="contact-page" />;
      default:
        return <HomePage onNavigate={navigateToPage} data-oid="7.8rphg" />;
    }
  };

  return (
    <ErrorBoundary data-oid="xh.m-g0">
      <AccessibilityProvider data-oid="ev:ath4">
        <div
          className="min-h-screen bg-black text-white relative overflow-hidden"
          data-oid="32va478"
        >
          {/* SEO Manager */}
          <SEOManager
            data={getCurrentSEOData()}
            page={currentPage}
            data-oid="b__dpi_"
          />

          {/* Skip Link for accessibility */}
          <SkipLink data-oid="i_-.2xt" />

          {/* Enhanced animated background elements with better contrast */}
          <div
            className="fixed inset-0 z-0"
            aria-hidden="true"
            data-oid="ui11_vo"
          >
            {/* Strong dark overlay for better text contrast */}
            <div className="absolute inset-0 bg-black" data-oid="35jj9:w"></div>
            <div
              className="absolute inset-0 bg-gradient-to-br from-black via-slate-950 to-black"
              data-oid="pi14cic"
            ></div>

            {/* Multiple floating orbs for depth - reduced opacity for better contrast */}
            <div
              className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/15 rounded-full filter blur-3xl animate-pulse"
              data-oid="7v1wm37"
            ></div>
            <div
              className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/15 rounded-full filter blur-3xl animate-pulse delay-1000"
              data-oid="k.q-k::"
            ></div>
            <div
              className="absolute top-3/4 left-1/2 w-96 h-96 bg-cyan-600/10 rounded-full filter blur-3xl animate-pulse delay-2000"
              data-oid="5dc:lw4"
            ></div>
            <div
              className="absolute top-1/2 right-1/3 w-64 h-64 bg-pink-600/10 rounded-full filter blur-2xl animate-pulse delay-3000"
              data-oid="t47fshx"
            ></div>
            <div
              className="absolute bottom-1/3 left-1/5 w-80 h-80 bg-indigo-600/12 rounded-full filter blur-3xl animate-pulse delay-4000"
              data-oid="_cghk0d"
            ></div>

            {/* Subtle grid pattern overlay */}
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.02)_1px,transparent_0)] bg-[length:50px_50px]"
              data-oid="1p13:.."
            ></div>

            {/* Additional dark overlay for maximum contrast */}
            <div
              className="absolute inset-0 bg-black/20"
              data-oid="1zho-es"
            ></div>
          </div>

          <div className="relative z-10" data-oid="w32v6hf">
            {/* Navigation with enhanced accessibility */}
            <Navigation
              currentPage={currentPage}
              onNavigate={navigateToPage}
              data-oid="fm.8f05"
            />

            {/* Main content area */}
            <main
              id="main-content"
              role="main"
              aria-label="Main content"
              className="focus:outline-none"
              tabIndex={-1}
              data-oid="m-f-hnb"
            >
              <AnimatePresence mode="wait" data-oid="7pq:kv2">
                <motion.div
                  key={currentPage}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    duration: 0.5,
                    ease: "easeInOut",
                    // Respect reduced motion preference
                    ...(window.matchMedia("(prefers-reduced-motion: reduce)")
                      .matches && {
                      duration: 0.01,
                    }),
                  }}
                  data-oid="-nrglnc"
                >
                  <Suspense
                    fallback={<LoadingSpinner data-oid="6szutzn" />}
                    data-oid="tutg2:j"
                  >
                    {renderPage()}
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </main>

            {/* Footer */}
            <Footer onNavigate={navigateToPage} data-oid="2ri9g:6" />
          </div>

          {/* Global Floating Chat Component */}
          <Chatbot />

          {/* Live region for announcements */}
          <div
            id="announcements"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            data-oid="rd3ybyf"
          ></div>

          {/* Schema.org structured data for better SEO */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebSite",
                name: "Inno Dot",
                description:
                  "Premier digital marketing agency specializing in SEO, PPC, social media marketing, web design, and AI automation",
                url: "https://innodot.tech",
                potentialAction: {
                  "@type": "SearchAction",
                  target: "https://innodot.tech/search?q={search_term_string}",
                  "query-input": "required name=search_term_string",
                },
                publisher: {
                  "@type": "Organization",
                  name: "Inno Dot",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://innodot.tech/logo.png",
                  },
                },
              }),
            }}
            data-oid="7qv.rrf"
          />
        </div>
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}
